// src/components/Layout.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { T } from './UI';

const NAV = [
  { k:'dashboard', icon:'⬛', label:'Dashboard' },
  { k:'orders',    icon:'📦', label:'Orders' },
  { k:'finance',   icon:'💰', label:'Finance' },
  { k:'invoices',  icon:'📄', label:'Invoices' },
  { k:'receipts',  icon:'🧾', label:'Receipts' },
  { k:'printing',  icon:'🖨️', label:'Printing' },
  { k:'logistics', icon:'🚚', label:'Logistics' },
  { k:'chat',      icon:'💬', label:'Chat' },
  { k:'pricelist', icon:'🏷️', label:'Price List' },
  { k:'website',   icon:'🌐', label:'Website', perm:'manage_website' },
  { k:'expenses',  icon:'💸', label:'Expenses', perm:'record_expenses' },
  { k:'audit',     icon:'📜', label:'Audit Trail' },
  { k:'reports',   icon:'📊', label:'Reports' },
  { k:'staff',     icon:'🧑‍💼', label:'Staff & Roles', perm:'manage_users' },
  { k:'settings',  icon:'⚙️', label:'Settings' },
];

const STAFF_COLORS = { sales:'#3B82F6', operations:'#8B5CF6', finance:'#22C55E', management:'#D4A017', production:'#F59E0B' };

export default function Layout({ page, onNav, children, alertCount = 0 }) {
  const { user, logout, can } = useAuth();
  const [notifOpen, setNotifOpen] = useState(false);
  const color = STAFF_COLORS[user?.role] || T.gold;

  return (
    <div style={{ minHeight:'100vh', background:T.bg, color:T.text, fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      {/* Header */}
      <div style={{ background:T.surface, borderBottom:`1px solid ${T.border}`, padding:'0 20px', height:'58px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
          <div style={{ fontSize:'13px', fontWeight:800, color:T.gold, letterSpacing:'1px' }}>✦ MASTER SOLVER</div>
          <div style={{ width:'1px', height:'18px', background:T.border }} />
          <div style={{ fontSize:'12px', color:T.muted }}>ERP Platform</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
          {/* Notification Bell */}
          <div style={{ position:'relative' }}>
            <button onClick={() => setNotifOpen(v => !v)}
              style={{ background:'none', border:'none', cursor:'pointer', fontSize:'18px', position:'relative', lineHeight:1, padding:'4px' }}>
              🔔
              {alertCount > 0 && (
                <span style={{ position:'absolute', top:'-2px', right:'-2px', background:T.danger, borderRadius:'50%', width:'15px', height:'15px', fontSize:'9px', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>{alertCount}</span>
              )}
            </button>
            {notifOpen && (
              <div style={{ position:'absolute', top:'38px', right:0, width:'280px', background:T.surface, border:`1px solid ${T.border}`, borderRadius:'12px', zIndex:150, boxShadow:'0 8px 32px #000000BB', padding:'12px 0' }}>
                <div style={{ padding:'0 16px 10px', fontWeight:700, fontSize:'13px', borderBottom:`1px solid ${T.border}` }}>🔔 Alerts</div>
                {alertCount === 0
                  ? <div style={{ padding:'14px 16px', fontSize:'12px', color:T.muted }}>All clear — no pending actions</div>
                  : <div style={{ padding:'14px 16px', fontSize:'12px', color:T.warning }}>{alertCount} item{alertCount>1?'s':''} need your attention</div>
                }
                <button onClick={() => setNotifOpen(false)} style={{ margin:'8px 16px 0', background:'none', border:'none', color:T.muted, fontSize:'12px', cursor:'pointer' }}>Close</button>
              </div>
            )}
          </div>
          {/* User */}
          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            <div style={{ width:30, height:30, borderRadius:'50%', background:`${color}22`, border:`2px solid ${color}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color }}>
              {user?.name?.slice(0,2).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize:'12px', fontWeight:700, color:T.text }}>{user?.name}</div>
              <div style={{ fontSize:'10px', color:T.muted, textTransform:'capitalize' }}>{user?.role}</div>
            </div>
          </div>
          <button onClick={logout} style={{ background:'transparent', color:T.muted, border:`1px solid ${T.border}`, borderRadius:'7px', padding:'5px 12px', fontSize:'12px', cursor:'pointer' }}>Sign Out</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ display:'flex' }}>
        {/* Sidebar */}
        <div style={{ width:'210px', background:T.surface, borderRight:`1px solid ${T.border}`, minHeight:'calc(100vh - 58px)', padding:'12px 0', flexShrink:0 }}>
          <div style={{ padding:'10px 18px 16px', borderBottom:`1px solid ${T.border}`, marginBottom:'6px' }}>
            <div style={{ fontSize:'10px', color:T.goldDim, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase' }}>Navigation</div>
          </div>
          {NAV.filter(n => !n.perm || can(n.perm)).map(n => (
            <div key={n.k} onClick={() => onNav(n.k)}
              style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 18px', cursor:'pointer', fontSize:'13px', fontWeight:page===n.k?700:400, color:page===n.k?T.gold:T.muted, background:page===n.k?`${T.gold}11`:'transparent', borderLeft:page===n.k?`3px solid ${T.gold}`:'3px solid transparent', transition:'all 0.15s' }}>
              <span style={{ fontSize:'14px' }}>{n.icon}</span>{n.label}
            </div>
          ))}
          <div style={{ padding:'14px 18px', marginTop:'12px', borderTop:`1px solid ${T.border}` }}>
            <div style={{ fontSize:'10px', color:T.muted, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Logged in as</div>
            <div style={{ fontSize:'12px', fontWeight:700, color }}>{user?.name}</div>
            <div style={{ fontSize:'11px', color:T.muted, textTransform:'capitalize', marginTop:'2px' }}>{user?.role}</div>
          </div>
        </div>

        {/* Main */}
        <div style={{ flex:1, padding:'22px', overflowY:'auto', maxHeight:'calc(100vh - 58px)' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
